import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { State } from '../../shared/core/state';
import * as CourseActions from '../../shared/core/state/course/course.actions';
import { CourseItem, CourseStatus } from '../../shared/core/state/course/models';
import { StorageService } from '../../shared/util/storage.service';
import { CONST } from '../../shared/util/const';
import { FormatterService } from '../../shared/util/formatter.service';
import { Resource } from '../../shared/core/state/resource/models';

@Component({
  selector: 'black-course-management',
  templateUrl: './course-management.component.html',
  styleUrls: ['./course-management.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class CourseManagementComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private formatter = inject(FormatterService);

  courses: CourseItem[] = [];
  filteredCourses: CourseItem[] = [];
  selectedStatus: string = 'ALL';
  searchQuery: string = '';
  isLoading = true;
  showInviteModal = false;
  selectedCourseIdForInvite?: string;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.store.dispatch(CourseActions.fetchAllForInstructor());

    this.store
      .select((state) => state.courses.courseItems)
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.courses = items || [];
        this.isLoading = false;
        this.applyFilters();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    this.applyFilters();
  }

  onStatusChange(status: string): void {
    this.selectedStatus = status;
    this.applyFilters();
  }

  applyFilters(): void {
    let result = [...this.courses];

    if (this.selectedStatus !== 'ALL') {
      result = result.filter(
        (c) => (c.status || CourseStatus.DRAFT).toUpperCase() === this.selectedStatus
      );
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.title?.toLowerCase().includes(q) ||
          c.subject?.toLowerCase().includes(q) ||
          c.targetAudience?.toLowerCase().includes(q) ||
          c.details?.descriptionSummary?.toLowerCase().includes(q)
      );
    }

    this.filteredCourses = result;
  }

  createCourse(): void {
    this.router.navigate(['/courses/add']);
  }

  editCourse(courseId: string): void {
    this.storageService.set(CONST.STORAGE_KEYS.CREATE_COURSE_ID, courseId);
    this.router.navigate(['/courses/edit']);
  }

  previewCourse(courseId: string): void {
    this.router.navigate(['/courses', courseId]);
  }

  getBannerUrl(bannerResource?: Resource | string): string {
    return this.formatter.getResourceImagePath(bannerResource || '');
  }

  formatPrice(price?: number): string {
    if (!price || price === 0) return 'Free';
    return `LKR ${price.toLocaleString('en-LK')}`;
  }

  getTotalLessons(course: CourseItem): number {
    if (!course.contents) return 0;
    return course.contents.reduce(
      (acc, content) => acc + (content.resources?.length || 0),
      0
    );
  }

  getDurationLabel(course: CourseItem): string {
    const hours = course.details?.totalDurationHour || 0;
    const minutes = course.details?.totalDurationMinutes || 0;
    if (hours === 0 && minutes === 0) return 'Self-Paced';
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  }

  openInviteModal(courseId?: string): void {
    this.selectedCourseIdForInvite = courseId;
    this.showInviteModal = true;
  }
}